import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, ElementRef, viewChild, inject, effect, signal, untracked, OnDestroy } from '@angular/core';
import { extend, injectStore, injectLoader, NgtArgs } from 'angular-three';
import { ArService } from '../../services/ar.service';
import { AudioService, ConfettiService } from '@kanek/shared-ui';
import { NgtsHTML, NgtsHTMLContent, NgtsHTMLOptions } from 'angular-three-soba/misc';
import { NgtsOrbitControls } from 'angular-three-soba/controls';
import { GLTFLoader } from 'three-stdlib';
import { UiDiceRollerComponent, DiceRollerConfig, DiceRollResult } from '@kanek/shared/ui-dice-roller';
import { calculateHTMLDimensions3D } from '../../utils/three-scene.utils';
import { EMPIRICAL_CORRECTIONS } from '../../constants/three-scene.constants';
import * as THREE from 'three';
import {
  BoxGeometry,
  ConeGeometry,
  PlaneGeometry,
  MeshStandardMaterial,
  ShadowMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  Vector3,
  Euler
} from 'three';
import { GameBoardSettings, BoardSpace, PIXELS_PER_UNIT, TOTAL_UNITS_PER_SIDE, CORNER_UNITS, SPACE_WIDTH_UNITS, SPACES_PER_SIDE, GAME_BOARD_PHYSICS } from './game-board.types';
import { GameBoardRenderer } from './game-board-renderer';
import { BOARD_SIZE_PIXELS } from '@kanek/shared-data-access';

// Extend Three.js objects for Angular Three
extend({
  BoxGeometry,
  ConeGeometry,
  PlaneGeometry,
  MeshStandardMaterial,
  ShadowMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  Vector3,
  Euler
});

/**
 * Game Board Scene Component
 *
 * Declarative Angular Three component that renders the complete board game in 3D:
 * - Box base (thin, board-sized)
 * - HTML board overlay (projected onto box top)
 * - 3D die (Cannon.js physics)
 * - 3D pawn game piece (GLB model)
 *
 * Architecture:
 * - Pure presentation component (stateless)
 * - All state passed via scene.userData or @Input()
 * - Reactive geometry using computed signals
 * - Follows Angular Three patterns
 */
@Component({
  selector: 'lib-game-board-scene',
  standalone: true,
  imports: [
    NgtArgs,
    NgtsHTML,
    NgtsHTMLContent,
    NgtsOrbitControls,
    UiDiceRollerComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!-- Three-Point Lighting System -->

    <!-- Ambient: Base illumination -->
    <ngt-ambient-light [intensity]="0.8" />

    <!-- Hemisphere: Natural sky/ground gradient lighting -->
    <ngt-hemisphere-light
      [skyColor]="16777215"
      [groundColor]="4473924"
      [intensity]="0.5"
    />

    <!-- Key Light: Main directional (strongest, casts shadows) -->
    <ngt-directional-light
      [position]="[5, 8, 5]"
      [intensity]="2.5"
      [castShadow]="true"
    />

    <!-- Fill Light: Softer secondary (reduces harsh shadows) -->
    <ngt-directional-light
      [position]="[-3, 4, -2]"
      [intensity]="0.8"
    />

    <!-- Rim Light: Back-highlight (adds depth and separation) -->
    <ngt-directional-light
      [position]="[0, 3, -6]"
      [intensity]="1.0"
    />

    <!-- OrbitControls (conditional on enableCameraControls) -->
    @if (enableCameraControls()) {
      <ngts-orbit-controls
        [options]="{
          enableDamping: true,
          dampingFactor: 0.05,
          enablePan: true
        }" />
    }

    <!-- Scene Group (rotation depends on orientationMode) -->
    <ngt-group [rotation]="sceneGroupRotation()">
      <!-- Board Base (thin box) -->
      <ngt-mesh
        [position]="[0, 0, 0]"
        [castShadow]="settingsData()?.castShadow ?? true"
        [receiveShadow]="true">
        <ngt-value [rawValue]="boardGeometry()" attach="geometry" />
        <ngt-value [rawValue]="boardMaterial()" attach="material" />
      </ngt-mesh>

      <!-- Dice Roller Component (replaces simple die mesh) -->
      <lib-dice-roller
        [config]="diceConfig()"
        (rollComplete)="onRollComplete($event)"
      />

      <!-- 3D Pawn (chess pawn GLB model with cone fallback) -->
      @if (pawnLoader(); as model) {
        <!-- Pawn GLB Model - using parameters input for transformations -->
        <ngt-primitive
          *args="[model.scene]"
          [parameters]="{
            position: pawnPosition(),
            rotation: pawnRotation(),
            scale: pawnScale(),
            castShadow: settingsData()?.castShadow ?? true
          }"
        />
      } @else {
        <!-- Fallback Cone Geometry -->
        <ngt-mesh
          [position]="pawnPosition()"
          [rotation]="[Math.PI / 2, 0, 0]"
          [castShadow]="settingsData()?.castShadow ?? true">
          <ngt-value [rawValue]="pawnGeometry()" attach="geometry" />
          <ngt-value [rawValue]="pawnMaterial()" attach="material" />
        </ngt-mesh>
      }

      <!-- Reflective Overlay Mesh (reflections) -->
      <ngt-mesh
        [position]="htmlReflectiveOverlayPosition()"
        [scale]="htmlReflectiveOverlayScale()"
        [receiveShadow]="true"
        [castShadow]="false">
        <ngt-plane-geometry *args="[1, 1]" />
        <ngt-mesh-standard-material
          [transparent]="true"
          [opacity]="0.1"
          [roughness]="0.0"
          [metalness]="1.0"
          [side]="FrontSide"
          [depthWrite]="false"
          [blending]="Blending"]
        />
      </ngt-mesh>

      <!-- Shadow Overlay Mesh -->
      <ngt-mesh
        [position]="htmlReflectiveOverlayPosition()"
        [scale]="htmlReflectiveOverlayScale()"
        [receiveShadow]="true"
        [castShadow]="false">
        <ngt-plane-geometry *args="[1, 1]" />
        <ngt-shadow-material
        [transparent]="true"
          [opacity]="0.1"
          [side]="FrontSide"
          [depthWrite]="false"
        />
      </ngt-mesh>

      <!-- HTML Board Overlay (LAST child for proper occlusion) -->
      <ngts-html [options]="htmlOverlayOptions()">
        <!-- Occlusion geometry: auto-sized by ngts-html to match HTML content -->
        <ngt-plane-geometry
          data-occlusion-geometry
        />
        <!-- Custom occlusion material to avoid shader compilation errors -->
        <ngt-mesh-basic-material
          data-occlusion-material
          [colorWrite]="false"
        />
        <div [ngtsHTMLContent]="htmlContentOptions()">
          <div
            #boardHost
            class="game-board-host"
            [style.width]="boardDimensions().width"
            [style.height]="boardDimensions().height">
          </div>
        </div>
      </ngts-html>
    </ngt-group>
  `,
  styles: [`
    :host {
      display: contents;
    }
  `]
})
export class GameBoardSceneComponent implements OnDestroy {
  // Inputs for direct usage
  settings = input<GameBoardSettings | null>(null);
  boardContainer = input<HTMLElement | null>(null);

  // ViewChild to get the rendered board host element
  private boardHost = viewChild<ElementRef<HTMLDivElement>>('boardHost');

  // ViewChild to get OrbitControls instance (when enabled)
  private orbitControlsRef = viewChild(NgtsOrbitControls);

  private store = injectStore();
  protected arService = inject(ArService);
  private audioService = inject(AudioService);
  private confettiService = inject(ConfettiService);

  // Store renderer instance to preserve game state across settings changes
  private boardRenderer: GameBoardRenderer | null = null;
  // Track current game position to restore after settings changes
  private currentGamePosition: number = 0;

  // Load Pawn.glb model
  protected pawnLoader = injectLoader(
    () => GLTFLoader,
    () => 'assets/models/Pawn.glb'
  );

  // Effect: Enable shadows on pawn model when loaded
  private enablePawnShadows = effect(() => {
    const model = this.pawnLoader();
    if (model?.scene) {
      // Traverse the model's scene graph and enable castShadow on all meshes
      model.scene.traverse((child: any) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = false; // Pawn shouldn't receive shadows (it's above the board)
        }
      });
    }
  });

  // Constants for template
  protected readonly Math = Math;
  protected readonly FrontSide = THREE.FrontSide;
  protected readonly Blending = THREE.AdditiveBlending;

  // Pawn positioned at START corner center (bottom-right)
  // START corner serves as both starting position and GRAND PRIZE space
  // Position calculated: center of 4x4 corner = 2 units from edges
  protected pawnPosition = signal<[number, number, number]>([4.3, -5.0, 0.36]); // Will be updated by calculateSpacePosition(0)

  // Computed: Settings from scene.userData or input
  protected settingsData = computed(() => {
    const inputSettings = this.settings();

    // Read scene via untracked to avoid subscribing to 60fps NgtStore clock updates.
    // The configSignal itself is still called in tracked context so reactive config changes work.
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    const sceneSettings = config?.['settings'];

    return inputSettings || sceneSettings || null;
  });

  // Computed: Items from scene.userData
  protected itemsData = computed(() => {
    // Read scene via untracked to avoid frame-reactive subscription.
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    return config?.['items'] || [];
  });

  // Computed: Base HTML dimensions converted to 3D units (includes 1% correction from shared utility)
  // store.get() is wrapped in untracked() so this computed does NOT re-run on every NgtStore
  // clock tick (which would recreate BoxGeometry/MeshStandardMaterial objects at 60Hz → GPU leak).
  protected htmlDimensions3D = computed<{ width: number; height: number }>(() => {
    const settings = this.settingsData();
    const boardSize: 'small' | 'medium' | 'large' = settings?.boardSize || 'medium';
    const pixelsPerUnit = PIXELS_PER_UNIT[boardSize];
    const boardPixels = TOTAL_UNITS_PER_SIDE * pixelsPerUnit;

    return calculateHTMLDimensions3D(boardPixels, boardPixels, untracked(() => this.store.get()));
  });

  // Computed: Board dimensions (box geometry uses width/height/depth, not radius)
  protected boardWidth = computed(() => {
    // Add 3% border margin on top of corrected HTML dimensions
    return this.htmlDimensions3D().width * EMPIRICAL_CORRECTIONS.BOARD_BORDER_MARGIN;
  });

  protected boardHeight = computed(() => {
    // Board is square
    return this.boardWidth();
  });

  // Computed: Playable board size (boardWidth without 3% margin)
  protected boardSize = computed(() => {
    return this.boardWidth() / EMPIRICAL_CORRECTIONS.BOARD_BORDER_MARGIN;
  });

  // Computed: Board pixel dimensions for HTML container
  protected boardDimensions = computed(() => {
    const settings = this.settingsData();
    const boardSize: 'small' | 'medium' | 'large' = settings?.boardSize || 'medium';
    // Calculate from units: totalUnits × pixelsPerUnit → boardPixels
    const pixelsPerUnit = PIXELS_PER_UNIT[boardSize];
    const boardPixels = TOTAL_UNITS_PER_SIDE * pixelsPerUnit; // e.g., 23 × 20 = 460px
    return {
      width: `${boardPixels}px`,
      height: `${boardPixels}px`
    };
  });

  // Computed: Board size scale factor (for scaling dice and pawn relative to board)
  protected boardSizeScaleFactor = computed(() => {
    const settings = this.settingsData();
    const boardSize: 'small' | 'medium' | 'large' = settings?.boardSize || 'medium';
    // Scale factor relative to medium (1000px baseline)
    // Small: 750px → 0.75x, Medium: 1000px → 1.0x, Large: 1250px → 1.25x
    const scaleFactor = BOARD_SIZE_PIXELS[boardSize] / BOARD_SIZE_PIXELS['medium'];
    return scaleFactor;
  });

  // Computed: Board depth (thickness of the board box)
  protected boardDepth = computed(() => {
    const settings = this.settingsData();
    return settings?.boxDepth ?? 0.2; // Thicker box by default (0.2 instead of 0.1)
  });

  // Computed: Board geometry (following scratchcard pattern)
  protected boardGeometry = computed(() => {
    return new BoxGeometry(
      this.boardWidth(),
      this.boardHeight(),
      this.boardDepth()
    );
  });

  protected boardMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.boardColor ?? '#654321', // Richer brown
      roughness: settings?.boardRoughness ?? 0.9, // More matte wood finish
      metalness: settings?.boardMetalness ?? 0.1, // Wood has minimal metallic properties
      envMapIntensity: 0.3 // Subtle environmental reflections
    });
  });

  // Computed: Dice configuration for dice-roller component
  protected diceConfig = computed<DiceRollerConfig>(() => {
    const settings = this.settingsData();
    const orientationMode = settings?.orientationMode || 'flat';
    const bypassActive = this.arService.shouldBypassAR;
    const boardDepth = this.boardDepth();

    // Floor is at the top surface of the board box
    const floorZ = boardDepth / 2 - 0.09;

    // Get scale factor for all scaling calculations
    const scaleFactor = this.boardSizeScaleFactor();

    // Start die above the floor - scale the offset with board size
    const startZ = floorZ + (GAME_BOARD_PHYSICS.DIE_START_HEIGHT_MULTIPLIER * scaleFactor);

    // Scene rotation for dice-roller (matches scene group rotation)
    let sceneRotation: [number, number, number] = [0, 0, 0];
    if (!bypassActive && orientationMode === 'vertical') {
      sceneRotation = [-Math.PI / 2, 0, 0];
    }

    // Perfect scaling approach:
    // - Die created at size 1.0, then scaled via mesh.scale (maintains geometry proportions)
    // - Mass scales by S³ (volume) to maintain constant density
    // - Impulse scales by S⁴ to maintain consistent angular velocity
    // - Gravity constant ensures proper proportional scaling
    // Result: Physics feels identical across small (750px), medium (1000px), large (1250px)

    // Tutorial params (constant for size=1.0 die)
    return {
      diceCount: 1,
      mode: 'predetermined',
      size: 1.0, // Always create at base size
      edgeRadius: 0.07,
      segments: 50,
      notchDepth: 0.1,
      notchRadius: 0.12,
      scale: scaleFactor, // Scale the entire mesh (0.75/1.0/1.25)
      gravity: [0, 0, -GAME_BOARD_PHYSICS.BASE_GRAVITY], // Constant gravity for perfect scaling
      floorPosition: [0, 0, floorZ], // Floor at board surface
      startPosition: [0, 0, startZ], // Start above floor (Z-axis is height)
      sceneRotation
    };
  });

  // Computed: Pawn geometry and material (fallback for when GLB not loaded)
  protected pawnGeometry = computed(() => {
    return new ConeGeometry(0.3, 0.8, 8);
  });

  protected pawnMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.pawnColor ?? '#4CAF50', // Configurable pawn color
      roughness: 0.4, // Slightly glossy painted finish
      metalness: 0.3, // Subtle metallic sheen
      envMapIntensity: 0.5 // Enhanced reflections for painted surface
    });
  });

  // Computed: Pawn scale for GLB model (from settings or default)
  protected pawnScale = computed<[number, number, number]>(() => {
    const settings = this.settingsData();
    // Apply board size scaling to maintain proportions across board sizes
    const boardScaleFactor = this.boardSizeScaleFactor();
    // Apply user's scale multiplier from settings
    const multiplier = settings?.pawnScale ?? 1.0;
    const scale = GAME_BOARD_PHYSICS.PAWN_BASE_SCALE * boardScaleFactor * multiplier;
    return [scale, scale, scale];
  });

  // Computed: Pawn rotation (from settings or default)
  protected pawnRotation = computed<[number, number, number]>(() => {
    const settings = this.settingsData();
    // Default: Rotate 90° on X axis (same as cone) to match AR board orientation
    return settings?.pawnRotation ?? [Math.PI / 2, 0, 0];
  });

  // Computed: Pawn Z offset (above board surface and HTML overlay)
  protected pawnZOffset = computed(() => {
    const boardDepth = this.boardDepth();
    // Position pawn above board surface and HTML overlay
    // Board top surface is at boardDepth/2, HTML at boardDepth/2 + 0.01, pawn slightly above that
    return boardDepth / 2 - 0.09;
  });

  // Computed: Board spaces from scene.userData
  protected boardSpaces = computed(() => {
    const scene = untracked(() => this.store.get()).scene;
    return (scene?.userData?.['boardSpaces'] as BoardSpace[]) || [];
  });

  // Computed: Scene group rotation (depends on orientationMode + AR state)
  protected sceneGroupRotation = computed<[number, number, number]>(
    () => {
      const settings = this.settingsData();
      const orientationMode = settings?.orientationMode || 'flat';
      const enableAR = untracked(() => this.store.get()).scene?.userData?.['enableAR'] ?? false;

      // Orientation setting only applies in AR mode
      // In non-AR mode (orbit controls), always show bird's eye view (flat on ground)
      if (!enableAR) {
        return [0, 0, 0]; // Bird's eye view for orbit controls
      }

      // In AR mode, apply rotation based on orientationMode
      if (orientationMode === 'flat') {
        // Flat: Board parallel to marker (lying flat on surface)
        return [-Math.PI / 2, 0, 0];
      } else {
        // Vertical: Board perpendicular to marker (standing up, facing user)
        // Rotate 180° around Y-axis to face front side toward camera
        return [Math.PI, 0, 0];
      }
    },
    { equal: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] }
  );

  // Computed: Enable camera controls
  protected enableCameraControls = computed(() => {
    const settings = this.settingsData();
    const fromSettings = settings?.enableCameraControls ?? false;
    const fromUserData = untracked(() => this.store.get()).scene?.userData?.['enableCameraControls'] ?? false;
    const bypassActive = this.arService.shouldBypassAR;
    return bypassActive || fromSettings || fromUserData;
  });

  // Computed: HTML overlay options
  protected htmlOverlayOptions = computed((): Partial<NgtsHTMLOptions> => {
    const boardDepth = this.boardDepth();
    // Z offset: Position HTML just in front of box face (like scratchcard)
    const htmlZOffset = boardDepth / 2 + 0.01;

    // Track board dimensions to trigger re-evaluation when size changes
    this.boardDimensions();

    return {
      occlude: 'blending',  // Use blending mode for proper depth sorting with die/pawn
      transform: true,
      scale: 1.0,  // Fixed scale (use preset board sizes instead)
      // Position centered on top of the box
      position: [0, 0, htmlZOffset] as [number, number, number],
      // No rotation - HTML sits flat on box by default
      rotation: [0, 0, 0] as [number, number, number]
    };
  });

  // Computed: HTML content options
  protected htmlContentOptions = computed(() => {
    return {
      distanceFactor: 0,  // HTML at pixel size
      zIndexRange: [100, 1] as [number, number]
    };
  });

  // Computed: Reflective overlay position (slightly in front of HTML)
  protected htmlReflectiveOverlayPosition = computed<[number, number, number]>(() => {
    const boardDepth = this.boardDepth();
    const htmlZOffset = boardDepth / 2 + 0.01;
    // Position overlay 0.01 units in front of HTML
    return [0, 0, htmlZOffset + 0.01];
  });

  // Computed: Reflective overlay scale (pixel-perfect match to HTML content)
  protected htmlReflectiveOverlayScale = computed<[number, number, number]>(() => {
    // Use corrected HTML dimensions directly (1% correction already applied in htmlDimensions3D)
    const dimensions = this.htmlDimensions3D();
    return [dimensions.width, dimensions.height, 1];
  });

  /**
   * Create a canvas texture with a number for die face
   */
  private createDieFaceMaterial(number: number): MeshStandardMaterial {
    // DEPRECATED: This method is no longer used
    // Dice-roller library uses procedural geometry with carved notches instead
    return new MeshStandardMaterial({ color: 0xffffff });
  }

  /**
   * Roll die to predetermined value using dice-roller library
   */
  async rollDie(targetValue: number): Promise<void> {
    const scene = untracked(() => this.store.get()).scene;
    const diceRoller = scene?.userData?.['diceRoller'];

    if (!diceRoller) {
      console.error('[GameBoardScene] Dice roller not found in scene.userData');
      return;
    }

    try {
      await diceRoller.roll(targetValue);
    } catch (error) {
      console.error('[GameBoardScene] Die roll failed:', error);
    }
  }

  /**
   * Reset dice state. Call when restarting game.
   */
  resetDice(): void {
    const scene = untracked(() => this.store.get()).scene;
    const diceRoller = scene?.userData?.['diceRoller'];

    if (diceRoller && typeof diceRoller.reset === 'function') {
      diceRoller.reset();
    }
  }

  /**
   * Reset the entire game board - resets game state and pawn position
   * Called by refresh button in Experience Studio
   */
  resetGame(): void {
    // Reset game position
    this.currentGamePosition = 0;

    // Reset pawn to start position
    const startPos = this.calculateSpacePosition(0);
    this.pawnPosition.set(startPos);

    // Reset dice
    this.resetDice();

    // Re-enable the CTA button via GameEngine's showCTA signal
    const scene = untracked(() => this.store.get()).scene;
    const showCTA = scene?.userData?.['showCTA'];
    if (showCTA) {
      showCTA.set(true);
    }
  }

  /**
   * Handle roll complete event from dice-roller component
   */
  protected onRollComplete(result: DiceRollResult): void {
    // Roll complete event handled
  }

  /**
   * Animate pawn movement to target position
   * Calculates 3D coordinates based on board space layout
   */
  async movePawn(targetPosition: number): Promise<void> {
    return new Promise((resolve) => {
      const duration = 800; // 800ms movement
      const startTime = Date.now();
      const startPos = this.pawnPosition();

      // Store current position for restoration after settings changes
      this.currentGamePosition = targetPosition;

      // Calculate target 3D position based on board space
      const targetPos = this.calculateSpacePosition(targetPosition);

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-in-out for smooth movement)
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        // Interpolate position with slight arc (add Y bounce)
        const arcHeight = 0.3; // Pawn jumps slightly during movement
        const arc = Math.sin(progress * Math.PI) * arcHeight;

        this.pawnPosition.set([
          startPos[0] + (targetPos[0] - startPos[0]) * eased,
          startPos[1] + (targetPos[1] - startPos[1]) * eased,
          targetPos[2] + arc // Keep same Z but add arc
        ]);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Ensure final position is exact
          this.pawnPosition.set(targetPos);
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Calculate 3D position for a board space
   * Uses boardWidth (minus 3% margin) divided by 23 units to get 3D distance per unit
   * - Each side: 2 corners (4 units) + 5 spaces (3 units) = 23 units total
   * - Positions calculated from center of each space/corner
   * - START corner (pos 0) serves as both starting position and GRAND PRIZE space
   */
  private calculateSpacePosition(position: number): [number, number, number] {
    const boardSize = this.boardSize(); // Playable board size (no margin)
    const halfBoard = boardSize / 2;
    const unitSize = boardSize / 23; // 3D distance per unit (e.g., 11.04/23 = 0.48)
    const pawnZ = this.pawnZOffset(); // Dynamic Z position based on board depth

    // Position 0: START corner (bottom-right) - center at 2 units from edges
    if (position === 0) {
      return [halfBoard - 2 * unitSize, -halfBoard + 2 * unitSize, pawnZ];
    }

    // Side 1 (bottom): positions 1-5 (moving left from START)
    if (position >= 1 && position <= 5) {
      const spaceIndex = position - 1; // 0-4
      const unitsFromRight = 4 + 3 * spaceIndex + 1.5; // Corner + spaces + center offset
      return [halfBoard - unitsFromRight * unitSize, -halfBoard + 2 * unitSize, pawnZ];
    }

    // Position 6: ROLL AGAIN corner (bottom-left) - center at 2 units from left/bottom
    if (position === 6) {
      return [-halfBoard + 2 * unitSize, -halfBoard + 2 * unitSize, pawnZ];
    }

    // Side 2 (left): positions 7-11 (moving up from bottom-left)
    if (position >= 7 && position <= 11) {
      const spaceIndex = position - 7; // 0-4
      const unitsFromBottom = 4 + 3 * spaceIndex + 1.5;
      return [-halfBoard + 2 * unitSize, -halfBoard + unitsFromBottom * unitSize, pawnZ];
    }

    // Position 12: ROLL AGAIN corner (top-left)
    if (position === 12) {
      return [-halfBoard + 2 * unitSize, halfBoard - 2 * unitSize, pawnZ];
    }

    // Side 3 (top): positions 13-17 (moving right from top-left)
    if (position >= 13 && position <= 17) {
      const spaceIndex = position - 13; // 0-4
      const unitsFromLeft = 4 + 3 * spaceIndex + 1.5;
      return [-halfBoard + unitsFromLeft * unitSize, halfBoard - 2 * unitSize, pawnZ];
    }

    // Position 18: ROLL AGAIN corner (top-right)
    if (position === 18) {
      return [halfBoard - 2 * unitSize, halfBoard - 2 * unitSize, pawnZ];
    }

    // Side 4 (right): positions 19-23 (moving down from top-right)
    if (position >= 19 && position <= 23) {
      const spaceIndex = position - 19; // 0-4
      const unitsFromTop = 4 + 3 * spaceIndex + 1.5;
      return [halfBoard - 2 * unitSize, halfBoard - unitsFromTop * unitSize, pawnZ];
    }

    // Fallback to START
    return [halfBoard - 2 * unitSize, -halfBoard + 2 * unitSize, pawnZ];
  }

  constructor() {
    // Effect: Expose rollDie API via scene.userData for GameBoardRenderer.
    // NOTE: store.get() is tracked (not untracked) so this effect re-runs when
    // the NgtCanvas scene becomes available (scene is null during constructor).
    // Only userData assignments happen here — no Three.js objects are created,
    // so the per-frame re-run cost is negligible.
    effect(() => {
      const scene = this.store.get().scene;
      if (scene) {
        scene.userData['rollDie'] = this.rollDie.bind(this);
      }
    });

    // Effect: Initialize pawn position when board size is known
    effect(() => {
      const boardSize = this.boardSize();
      if (boardSize > 0) {
        const startPos = this.calculateSpacePosition(0);
        this.pawnPosition.set(startPos);
      }
    });

    // Effect: Apply pawn customization when model loads or settings change
    effect(() => {
      const model = this.pawnLoader();
      const settings = this.settingsData();

      // Get computed values (with defaults applied)
      const computedRotation = this.pawnRotation();
      const computedScale = this.pawnScale();

      // console.log('[GameBoardScene] Pawn effect triggered:', {
      //   loaded: !!model,
      //   hasScene: !!model?.scene,
      //   position: this.pawnPosition(),
      //   rawSettings: {
      //     color: settings?.pawnColor,
      //     scale: settings?.pawnScale,
      //     rotation: settings?.pawnRotation
      //   },
      //   computedValues: {
      //     rotation: computedRotation,
      //     scale: computedScale
      //   }
      // });

      // Apply color to pawn model materials (default: #4CAF50 green, or custom from settings)
      if (model?.scene) {
        const pawnColor = settings?.pawnColor ?? '#4CAF50'; // Default green
        model.scene.traverse((child: any) => {
          if (child.isMesh && child.material) {
            // Clone material to avoid affecting other instances
            if (Array.isArray(child.material)) {
              child.material = child.material.map((mat: any) => {
                const cloned = mat.clone();
                cloned.color.set(pawnColor);
                return cloned;
              });
            } else {
              child.material = child.material.clone();
              child.material.color.set(pawnColor);
            }
          }
        });
      }
    });

    // Effect: Initialize board when boardHost is ready and we have config
    effect(() => {
      const host = this.boardHost();
      const settings = this.settingsData();
      const items = this.itemsData();

      if (host?.nativeElement && settings && items && items.length >= 8) {
        this.initializeBoard(host.nativeElement, settings, items);
      }
    });

    // Effect: Initialize pawn position when board size is known
    effect(() => {
      const boardSize = this.boardSize();
      if (boardSize > 0) {
        const startPos = this.calculateSpacePosition(0);
        this.pawnPosition.set(startPos);
      }
    });

    // Effect: Store OrbitControls reference in scene.userData when ready
    effect(() => {
      const scene = untracked(() => this.store.get()).scene;
      const controls = this.orbitControlsRef();
      if (scene && controls) {
        const orbitControls = controls.controls();
        scene.userData['orbitControls'] = orbitControls;
      }
    });

    // Effect: Expose animation methods and services to scene.userData for renderer.
    // NOTE: store.get() is tracked so this effect re-runs when the scene becomes
    // available. Only cheap userData assignments — no Three.js object creation.
    effect(() => {
      const scene = this.store.get().scene;
      if (scene) {
        scene.userData = scene.userData || {};
        scene.userData['rollDie'] = (value: number) => this.rollDie(value);
        scene.userData['movePawn'] = (position: number) => this.movePawn(position);
        scene.userData['resetDice'] = () => this.resetDice();
        scene.userData['resetGame'] = () => this.resetGame();
        scene.userData['confettiService'] = this.confettiService;
        scene.userData['audioService'] = this.audioService;
      }
    });
  }

  ngOnDestroy(): void {
    // Dispose Three.js GPU resources to prevent memory leaks
    this.boardGeometry()?.dispose();
    this.pawnGeometry()?.dispose();
    (this.boardMaterial() as MeshStandardMaterial | null)?.dispose();
    (this.pawnMaterial() as MeshStandardMaterial | null)?.dispose();
  }

  /**
   * Initialize game board directly in the boardHost div
   * Used when GameBoardScene is loaded via GameEngine sceneGraph
   */
  private async initializeBoard(
    container: HTMLElement,
    settings: GameBoardSettings,
    items: Array<{
      id?: string;
      name?: string;
      label?: string;
      probability?: number;
      value?: string;
      icon?: string;
      [key: string]: unknown
    }>
  ): Promise<void> {
    try {
      // Check if reset was requested (from refresh button)
      const resetRequested = (settings as any)._resetRequested;
      if (resetRequested && this.boardRenderer) {
        this.resetGame();

        // Clear the reset flag to prevent re-triggering
        const scene = this.store.get().scene;
        const configSignal = scene?.userData?.['configSignal'];
        if (configSignal) {
          const currentConfig = configSignal();
          const newSettings = { ...currentConfig.settings };
          delete (newSettings as any)._resetRequested;
          configSignal.set({ ...currentConfig, settings: newSettings });
        }
      }

      // Create renderer instance if it doesn't exist
      if (!this.boardRenderer) {
        this.boardRenderer = new GameBoardRenderer();
        this.boardRenderer.setArService(this.arService);
      }

      // Convert items to prize format
      const prizes = items.map((item, index) => ({
        id: item.id || `${index + 1}`,
        label: item.name || item.label || `Prize ${index + 1}`,
        value: item.value || item.name || item.label || `Prize ${index + 1}`,
        icon: item.icon,
        probability: item.probability ?? (1 / items.length)
      }));

      // Initialize the renderer with the container
      this.boardRenderer.initialize(container, this.arService);

      // Add _previewPrizes to settings for demo mode
      const configSettings = {
        ...settings,
        _previewPrizes: prizes
      } as any;

      // Update renderer config to generate board HTML
      await this.boardRenderer.updateConfig({
        settings: configSettings,
        items: prizes
      });

      // Restore pawn position if game was in progress
      if (this.currentGamePosition > 0) {
        const restoredPos = this.calculateSpacePosition(this.currentGamePosition);
        this.pawnPosition.set(restoredPos);
      }
    } catch (error) {
      console.error('[GameBoardScene] Failed to initialize board:', error);
    }
  }
}
